import { useEffect, useState } from 'react';
import { Search } from 'lucide-react';

import { Input } from '@/components/ui/input';
import { CONVERSATION_SEARCH_MAX_LENGTH } from '@/features/conversations/conversations.api';
import { cn } from '@/lib/utils';

interface ConversationSearchInputProps {
  /** Debounced value reported to the parent. Update fires 300ms after the
   *  user stops typing. */
  onDebouncedChange: (value: string) => void;
  className?: string;
}

/**
 * Search input above the conversation list.
 *
 *   - Local controlled state for snappy typing feedback.
 *   - 300ms debounce before lifting to the parent — each lifted value is a
 *     distinct queryKey AND a real server round-trip now (the hub filters via
 *     `q=` on `/api/v1/conversations`), so the debounce is what keeps a
 *     keystroke burst from turning into a request burst.
 *
 * The lifted value flows `ConversationList` → `useConversations` → the
 * backend's `q=` param; `ConversationList` additionally guard-filters the
 * rendered rows with the same semantics (`matchesSearch` — see
 * search-match.ts) because the WS projector can inject rows into a searched
 * list's cache.
 */
export function ConversationSearchInput({
  onDebouncedChange,
  className,
}: ConversationSearchInputProps): React.JSX.Element {
  const [value, setValue] = useState('');

  useEffect(() => {
    const handle = window.setTimeout(() => {
      onDebouncedChange(value);
    }, 300);
    return () => window.clearTimeout(handle);
  }, [value, onDebouncedChange]);

  return (
    <div className={cn('relative', className)}>
      <Search
        className="pointer-events-none absolute left-3.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground"
        aria-hidden="true"
      />
      <Input
        type="search"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="Search by name or phone"
        aria-label="Search conversations"
        // The backend 422s a `q=` beyond 64 chars, and a failed list query
        // replaces the ENTIRE inbox with an error screen — stop over-length
        // input (typed or pasted) at the source. `useConversations` clamps
        // the normalized value again as the wire-level backstop.
        maxLength={CONVERSATION_SEARCH_MAX_LENGTH}
        className="h-9 rounded-[13px] pl-9 text-[13px]"
      />
    </div>
  );
}
